import type { CmsAdapter, CmsPage, CmsRendering } from '@eneco/cms-adapter';
import { CmsPageNotFoundError } from '@eneco/cms-adapter';
import type { Locale } from '@eneco/cms-types';

import { COLLISION_NAMES } from './collision-names.gen';
import { liftFieldGroups, liftReferencedItemGroups } from './field-groups';
import type { JssLayoutServiceData, JssRendering } from './jss-types';
import { toImage, toRichText, unwrapFields } from './mappers';

interface SitecoreAdapterConfig {
  layoutServiceUrl: string;
  apiKey: string;
  /**
   * The Sitecore site to resolve paths in, sent as `sc_site` as the legacy layout client does. Without
   * it the layout service falls back to its default site, which only matches nl/eneco/main.
   */
  site?: string;
  /**
   * The container's own rules for which site, language and item a request asks for, as the legacy lane
   * applies them (`transformFunctions`, e.g. Over ons' pages living in a site of their own). Without it
   * every request goes to `site` as it is.
   */
  transform?: (request: SitecoreRequest) => SitecoreRequest;
  fetchImpl?: typeof fetch;
}

/** What a layout service request asks for: `sc_site`, `sc_lang` and `item`. */
export interface SitecoreRequest {
  site: string;
  locale: Locale;
  item: string;
}

/**
 * Concrete Sitecore implementation of the `CmsAdapter` contract.
 *
 * This is the ONLY package outside `libs/sitecore/*` allowed to know about
 * JSS. ESLint enforces this via `scope:cms-sitecore`.
 */
export const createSitecoreAdapter = (config: SitecoreAdapterConfig): CmsAdapter => {
  const fetchImpl = config.fetchImpl ?? fetch;

  const fetchLayout = async (path: string, requestedLocale: Locale): Promise<JssLayoutServiceData> => {
    const asked = { site: config.site ?? '', locale: requestedLocale, item: path };
    const { site, locale, item } = config.transform?.(asked) ?? asked;
    const url = new URL(config.layoutServiceUrl);
    url.searchParams.set('item', item);
    url.searchParams.set('sc_lang', locale);
    url.searchParams.set('sc_apikey', config.apiKey);
    if (site) url.searchParams.set('sc_site', site);
    const response = await fetchImpl(url);
    if (!response.ok) {
      // A real 404 is a genuine content miss; anything else (500, network) is a transport failure.
      // Typed so `@eneco/cms-next`'s resolve-cms-page.ts can distinguish the two — see @eneco/cms-adapter.
      if (response.status === 404) {
        throw new CmsPageNotFoundError(`Sitecore layout service 404 for ${path}`);
      }
      throw new Error(`Sitecore layout service ${response.status} for ${path}`);
    }
    const data = (await response.json()) as JssLayoutServiceData;
    // Sitecore's other not-found convention: 200 OK with no route for the requested item
    // (matches getPageContent.ts's own `notFound = statusCode === 404 || route === null` check).
    // Guarded here rather than left to `toRendering`, which would otherwise throw an opaque
    // TypeError reading `.componentName` off `null`.
    if (data.sitecore.route === null) {
      throw new CmsPageNotFoundError(`Sitecore layout service returned no route for ${path}`);
    }
    return data;
  };

  const toRendering = (rendering: JssRendering): CmsRendering => {
    const mapped = toCmsRendering(rendering);
    // Hidden from serialization and spreads: see `CmsRendering.rawFields`.
    if (rendering.fields) Object.defineProperty(mapped, 'rawFields', { value: rendering.fields, enumerable: false });
    return mapped;
  };

  const toCmsRendering = (rendering: JssRendering): CmsRendering => ({
    componentName: rendering.componentName,
    uid: rendering.uid ?? cryptoId(),
    // Unwrapped first, then lifted: the raw fields are what say which keys were datasource
    // children, which is how a field group is told from a field whose value happens to be an object.
    // The items it references get the same lift as the rendering itself, before the rendering's own.
    // The collision names are keyed by Sanity type, which is what a rendering's `componentName` is.
    content: liftFieldGroups(
      liftReferencedItemGroups(
        unwrapFields(rendering.fields as Record<string, unknown> | undefined),
        rendering.fields as Record<string, unknown> | undefined,
      ),
      rendering.fields as Record<string, unknown> | undefined,
      Object.hasOwn(COLLISION_NAMES, rendering.componentName) ? COLLISION_NAMES[rendering.componentName] : undefined,
    ),
    // Only set when present, like the context below: an `undefined` value fails serialization.
    ...(rendering.placeholders
      ? {
          placeholders: Object.fromEntries(
            Object.entries(rendering.placeholders).map(([name, children]) => [
              name,
              // A rendering that needs a datasource but was placed without one is skipped, as the legacy
              // Placeholder does (`libs/sitecore/common/src/core/Placeholder.tsx`), so it neither renders
              // nor counts as a child.
              children.filter(child => !(child.datasourceRequired && !child.fields)).map(toRendering),
            ]),
          ),
        }
      : {}),
    ...(rendering.params ? { params: rendering.params } : {}),
  });

  const toPage = (raw: unknown): CmsPage => {
    const data = raw as JssLayoutServiceData;
    // `fetchLayout` already guards this for `client.getPage`/`getPlaceholder`; guarded again here
    // since `toPage` is public on the adapter and callable directly with an unguarded payload.
    if (data.sitecore.route === null) {
      throw new CmsPageNotFoundError('Sitecore layout service returned no route');
    }
    const ctx = data.sitecore.context;
    return {
      context: {
        locale: ctx.language as Locale,
        pageEditing: ctx.pageState !== 'normal',
        pagePath: ctx.itemPath ?? '/',
        // Left out rather than set to `undefined`: the page goes through `getServerSideProps`, which
        // cannot serialize `undefined`, and a guest has no customerId. Same as the Sanity adapter.
        ...(ctx.customerId !== undefined ? { customerId: ctx.customerId } : {}),
        ...(ctx.accountId !== undefined ? { accountId: ctx.accountId } : {}),
        ...(ctx.basePath !== undefined ? { basePath: ctx.basePath } : {}),
        ...(ctx.experimentInfo?.name && ctx.experimentInfo.variant
          ? { experimentInfo: { name: ctx.experimentInfo.name, variant: ctx.experimentInfo.variant } }
          : {}),
        cmsSource: 'sitecore',
      },
      // A route has no `componentName`; its template names the page type, as `_type` does on Sanity.
      root: toRendering({
        ...data.sitecore.route,
        componentName: data.sitecore.route.componentName ?? data.sitecore.route.templateName ?? 'route',
      }),
    };
  };

  return {
    client: {
      getPage: async ({ path, locale }) => toPage(await fetchLayout(path, locale)),
      getPlaceholder: async ({ path, placeholderName, locale }) => {
        const page = toPage(await fetchLayout(path, locale));
        return page.root.placeholders?.[placeholderName] ?? [];
      },
    },
    toPage,
    toRendering: (raw: unknown) => toRendering(raw as JssRendering),
    toImage,
    toRichText,
  };
};

let counter = 0;
const cryptoId = (): string => `r-${++counter}`;
